import React, { useEffect, useState } from "react";
import { Button, Form, FormField, FormGroup, Grid, GridColumn, Header, Input, Segment, Select } from "semantic-ui-react";
import { toast } from "react-toastify";
import Order from "../../../types/entities/Order";
import { AGE_RANGES_OPTIONS, GENDER_OPTIONS, MONTH_OPTIONS } from "../../../utils/dropdownOptions";
import { createOrderClientPerson, updateOrderClientPerson } from "../../../services/orders.service";

interface Props {
    order: Order | null;
    setOrder: (prospect: Order | null) => void;
}

interface RequestData {
    name?: string;
    paternalSurname?: string;
    maternalSurname?: string;
    gender?: string;
    age?: string;
    phone?: string;
    birthDay?: number;
    birthMonth?: number;
    birthYear?: number;
}

const OrderClientPersonForm = ({ order, setOrder }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [requestData, setRequestData] = useState<RequestData | undefined>(undefined);

    useEffect(() => {
        setRequestData({
            name: order?.client?.person?.name || "",
            paternalSurname: order?.client?.person?.paternalSurname || "",
            maternalSurname: order?.client?.person?.maternalSurname || "",
            gender: order?.client?.person?.gender || "",
            age: order?.client?.person?.age || "",
            phone: order?.client?.person?.phone || "",
            birthDay: order?.client?.person?.birthDay,
            birthMonth: order?.client?.person?.birthMonth,
            birthYear: order?.client?.person?.birthYear,
        });
    }, [order]);

    const handleChange = (e: any, { name, value }: any) => {
        if (name === "phone" && !validatePhone(value)) return;
        setRequestData({
            ...requestData,
            [name]: value ?? "",
        } as RequestData);
    };

    const validatePhone = (phone: string) => {
        const regex = /^[0-9]+$/;
        if (phone.length > 10) {
            return false;
        }
        if (phone !== "" && !regex.test(phone)) {
            return false;
        }
        return true;
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {

            if (!order?.id) {
                throw new Error("El pedido no puede ser undefined.");
            }

            if (requestData?.phone?.length !== 10) {
                throw new Error("El telefono no es valido, favor de verificar que sean 10 digitos sin espacios.");
            }

            if (order?.client?.person?.id) {
                const response = await updateOrderClientPerson(order.id, order.client.person.id, requestData);
                if (response?.status === 200) {
                    setOrder(response.data);
                    toast.success("Datos personales del pedido han sido actualizados con exito");
                } else {
                    throw new Error("El pedido no ha sido actualizado exitosamente, favor de contactar al area de TI");
                }
            } else {
                const response = await createOrderClientPerson(order.id, requestData);
                if (response?.status === 201) {
                    setOrder(response.data);
                    toast.success("Datos personales del pedido han sido guardados con exito");
                } else {
                    throw new Error("El pedido no ha sido creado exitosamente, favor de contactar al area de TI");
                }
            }

        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Grid>
                <GridColumn>
                    <Segment attached='top' color="grey" style={{ marginTop: 15 }}>
                        <Grid>
                            <GridColumn style={{ paddingTop: 5, paddingBottom: 5 }} verticalAlign="middle">
                                <Header as='h4' floated="left" style={{ marginTop: 8, marginBottom: 7 }}>Datos personales</Header>
                                {order?.client?.person?.id ? (
                                    <Button floated="right" color="yellow" type="submit" style={{ margin: 0 }}>Guardar</Button>
                                ) : (
                                    <Button floated="right" primary type="submit" style={{ margin: 0 }}>Guardar</Button>
                                )}
                            </GridColumn>
                        </Grid>
                    </Segment>
                    <Segment attached='bottom' loading={isSubmitting}>
                        <FormGroup widths='equal'>
                            <FormField
                                control={Input}
                                label='Nombre'
                                placeholder='Nombre(s)'
                                name="name"
                                value={requestData?.name}
                                onChange={handleChange}
                                required
                            />
                            <FormField
                                control={Input}
                                label='Apellido paterno'
                                placeholder='Apellido paterno'
                                name="paternalSurname"
                                value={requestData?.paternalSurname}
                                onChange={handleChange}
                                required
                            />
                            <FormField
                                control={Input}
                                label='Apellido materno'
                                placeholder='Apellido materno'
                                name="maternalSurname"
                                value={requestData?.maternalSurname}
                                onChange={handleChange}
                            />
                        </FormGroup>
                        <FormGroup widths='equal'>
                            <FormField
                                control={Select}
                                label='Genero'
                                options={GENDER_OPTIONS}
                                placeholder='Selecciona una opción...'
                                name="gender"
                                value={requestData?.gender}
                                onChange={handleChange}
                                clearable
                                required
                            />
                            <FormField
                                control={Select}
                                label='Edad'
                                options={AGE_RANGES_OPTIONS}
                                placeholder='Selecciona una opción...'
                                name="age"
                                value={requestData?.age}
                                onChange={handleChange}
                                clearable
                                required
                            />
                            <FormField
                                control={Input}
                                label='Telefono'
                                placeholder='10 caracteres numericos sin espacios ni guiones'
                                name="phone"
                                value={requestData?.phone}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup widths='equal'>
                            <FormField
                                control={Input}
                                type="number"
                                min={1}
                                max={31}
                                label='Dia de cumpleaños'
                                placeholder='Ejemplo: 14'
                                name="birthDay"
                                value={requestData?.birthDay}
                                onChange={handleChange}
                            />
                            <FormField
                                control={Select}
                                options={MONTH_OPTIONS}
                                label='Mes de cumpleaños'
                                placeholder='Selecciona una opción...'
                                name="birthMonth"
                                value={requestData?.birthMonth}
                                onChange={handleChange}
                            />
                            <FormField
                                control={Input}
                                type="number"
                                min={1900}
                                max={2026}
                                label='Año de nacimiento'
                                placeholder='Ejemplo: 1998'
                                name="birthYear"
                                value={requestData?.birthYear}
                                onChange={handleChange}
                            />
                        </FormGroup>
                    </Segment>
                </GridColumn>
            </Grid>
        </Form>
    );
};

export default OrderClientPersonForm;
